import { apiDelete, apiGet, apiPut, API_BASE_URL } from '../utils/apiClient';
import { ProjectRole } from '../roles';
import { ProjectMember } from '../components/projects/types';
import { mapUserRole } from './projects';

export type AssignableUser = Omit<ProjectMember, 'role'>;

interface ListProjectMembersResponseDto {
  members?: Array<{
    userId?: string;
    role?: string;
    name?: string;
    email?: string;
  }>;
}

interface SearchAssignableUsersResponseDto {
  users?: Array<{ userId?: string; name?: string; email?: string }>;
}

const WIRE_ROLE_PREFIX = 'PROJECT_ROLE_';

export const DEFAULT_ASSIGNABLE_USER_LIMIT = 10;
export const MIN_ASSIGNABLE_USER_QUERY_LENGTH = 2;

export function roleToWire(role: ProjectRole): string {
  return `${WIRE_ROLE_PREFIX}${role.toUpperCase()}`;
}

export function roleFromWire(wireRole?: string): ProjectRole | undefined {
  return wireRole?.startsWith(WIRE_ROLE_PREFIX)
    ? mapUserRole(wireRole.slice(WIRE_ROLE_PREFIX.length))
    : undefined;
}

function projectPath(projectId: string) {
  return `${API_BASE_URL}/projects/${encodeURIComponent(projectId)}`;
}

function memberPath(projectId: string, userId: string) {
  return `${projectPath(projectId)}/members/${encodeURIComponent(userId)}`;
}

export async function listProjectMembers(
  projectId: string,
): Promise<ProjectMember[]> {
  const response = await apiGet(`${projectPath(projectId)}/members`);
  const data = (await response.json()) as ListProjectMembersResponseDto;

  // A member whose role this client cannot name is dropped rather than shown
  // with a guessed role, which would invite editing it into something wrong.
  return (data.members ?? []).flatMap(member => {
    const role = roleFromWire(member.role);
    return member.userId && role
      ? [
          {
            id: member.userId,
            name: member.name ?? '',
            email: member.email ?? '',
            role,
          },
        ]
      : [];
  });
}

export async function setProjectMemberRole(
  projectId: string,
  userId: string,
  role: ProjectRole,
): Promise<void> {
  await apiPut(memberPath(projectId, userId), { role: roleToWire(role) });
}

export async function removeProjectMember(
  projectId: string,
  userId: string,
): Promise<void> {
  await apiDelete(memberPath(projectId, userId));
}

export async function searchAssignableUsers(
  projectId: string,
  query: string,
  options: { limit?: number; signal?: AbortSignal } = {},
): Promise<AssignableUser[]> {
  const params = new URLSearchParams({
    q: query,
    limit: String(options.limit ?? DEFAULT_ASSIGNABLE_USER_LIMIT),
  });
  const response = await apiGet(
    `${projectPath(projectId)}/assignable-users?${params.toString()}`,
    { signal: options.signal },
  );
  const data = (await response.json()) as SearchAssignableUsersResponseDto;

  return (data.users ?? []).flatMap(user =>
    user.userId
      ? [{ id: user.userId, name: user.name ?? '', email: user.email ?? '' }]
      : [],
  );
}
